/**
 * OZN Collection Grid
 * -----------------------------------------------------------------------------
 * Two cooperating features for the collection grid:
 *
 * 1) REVEAL (always on): a single, deterministic fade-in for product cards.
 *    The theme animates each card with TWO independent AOS triggers (the grid
 *    item `row-of-N` + the inner `<image-element>` `image-fade-in`). Because
 *    lazy images shift layout after AOS caches positions, the two triggers fire
 *    out of sync and the fade looks random. We neutralise the theme's per-image
 *    fade inside #CollectionAjaxContent (see section CSS) and reveal each card
 *    once, column-staggered, via one IntersectionObserver.
 *
 * 2) AUTOFILL (only when [data-ozn-autofill] is present, i.e. infinite scroll
 *    enabled): tops up each view to "per page" cards via the Section Rendering
 *    API and restores scroll position when returning from a product page.
 *    The "X items" label stays on the Liquid total (collection.products_count).
 *
 * Progressive enhancement: numbered pagination still works without JS, and a
 * safety timeout / <noscript> in the section CSS reveals cards if JS fails.
 * Re-binds after the theme's AJAX filter/sort ('collection:reloaded').
 */
(function () {
  'use strict';

  var SELECTORS = {
    container: '#CollectionAjaxContent',
    wrapper: '[data-ozn-autofill]',
    grid: '.grid--uniform',
    card: '.grid-product',
    pagination: '.pagination',
    loadMore: '.ozn-autofill-loadmore',
    loadMoreBtn: '.ozn-autofill-loadmore__btn'
  };

  var STORAGE_KEY = 'ozn:collection-scroll';
  var STATE_MAX_AGE = 30 * 60 * 1000; // 30 minutes
  var STAGGER_MS = 60; // per-column delay
  var STAGGER_MAX_COL = 6; // cap so wide/edge cases don't over-delay
  // Cap how many pages the INITIAL auto-fill may load in one burst. Without a
  // cap, collections that hide many sold-out variants render few cards per page,
  // so filling to "per page" cards could load every remaining page at once ->
  // huge DOM + repeated re-init -> the tab freezes. Beyond this cap the rest
  // loads on demand (scroll / "load more"), one page at a time.
  var MAX_FILL_PAGES = 4;

  var runtime = {
    inFlight: false,
    current: null,
    boundGlobals: false,
    boundResize: false,
    bootPending: false
  };

  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  function qs(root, sel) {
    return (root || document).querySelector(sel);
  }

  function qsa(root, sel) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function getContainer() {
    return document.querySelector(SELECTORS.container);
  }

  function getGrids() {
    var container = getContainer();
    return container ? qsa(container, SELECTORS.grid) : [];
  }

  function getWrapper() {
    return (
      document.querySelector(SELECTORS.container + ' ' + SELECTORS.wrapper) ||
      document.querySelector(SELECTORS.wrapper)
    );
  }

  function cardCount(grid) {
    return grid ? grid.querySelectorAll(SELECTORS.card).length : 0;
  }

  // ===========================================================================
  // REVEAL controller
  // ===========================================================================

  var Reveal = {
    io: null,

    ensureIO: function () {
      if (this.io || !('IntersectionObserver' in window)) return this.io;
      this.io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('ozn-in');
              Reveal.io.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0 }
      );
      return this.io;
    },

    // Assign a deterministic per-column transition-delay so each row fades
    // left-to-right. Rows are detected by shared offsetTop.
    assignDelays: function (grid) {
      var cards = qsa(grid, SELECTORS.card);
      var rowTop = null;
      var col = 0;
      cards.forEach(function (card) {
        var top = card.offsetTop;
        if (rowTop === null || Math.abs(top - rowTop) > 4) {
          rowTop = top;
          col = 0;
        } else {
          col += 1;
        }
        var capped = col > STAGGER_MAX_COL ? STAGGER_MAX_COL : col;
        card.style.transitionDelay = capped * STAGGER_MS + 'ms';
      });
    },

    setup: function (grid) {
      if (!grid) return;
      window.__oznRevealReady = true;

      if (!('IntersectionObserver' in window) || prefersReducedMotion()) {
        this.revealAll(grid);
        return;
      }

      this.assignDelays(grid);
      var io = this.ensureIO();
      qsa(grid, SELECTORS.card).forEach(function (card) {
        if (card.__oznObserved || card.classList.contains('ozn-in')) return;
        card.__oznObserved = true;
        io.observe(card);
      });
    },

    // Show every card immediately (no fade) — used for reduced motion and when
    // restoring a previous session.
    revealAll: function (grid) {
      if (!grid) return;
      window.__oznRevealReady = true;
      qsa(grid, SELECTORS.card).forEach(function (card) {
        card.style.transitionDelay = '0ms';
        card.classList.add('ozn-in');
        card.__oznObserved = true;
      });
    }
  };

  function bindResize() {
    if (runtime.boundResize) return;
    runtime.boundResize = true;
    var t;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(function () {
        getGrids().forEach(function (grid) {
          Reveal.assignDelays(grid);
        });
      }, 200);
    });
  }

  // ===========================================================================
  // Section Rendering API (autofill)
  // ===========================================================================

  function buildUrl(sectionId, page) {
    var params = new URLSearchParams(window.location.search);
    params.delete('section_id');
    params.delete('_oznc');
    params.set('page', String(page));
    return (
      window.location.pathname +
      '?section_id=' +
      encodeURIComponent(sectionId) +
      '&' +
      params.toString()
    );
  }

  function fetchPageCards(sectionId, page) {
    return fetch(buildUrl(sectionId, page), { credentials: 'same-origin' })
      .then(function (res) {
        return res.ok ? res.text() : Promise.reject(res.status);
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var grid =
          qs(doc, SELECTORS.wrapper + ' ' + SELECTORS.grid) ||
          qs(doc, SELECTORS.grid);
        if (!grid) return [];
        return qsa(grid, SELECTORS.card);
      });
  }

  // Wire quick-shop preloading for ONLY the newly appended cards. The theme's
  // own theme.initQuickShop()/reinitProductGridItem() re-scan EVERY .grid-product
  // on the page and re-bind listeners each call (plus AOS.refreshHard()), which
  // is O(n^2) across appends and freezes the tab. We bind once per new card and
  // skip AOS entirely (the grid's AOS image-fade is neutralised by section CSS;
  // the Reveal controller handles the fade).
  function initNewCardsQuickShop(cards) {
    if (!window.theme || !theme.settings || !theme.settings.quickView) return;
    if (typeof theme.preloadProductModal !== 'function') return;
    cards.forEach(function (card) {
      if (card.__oznQs) return;
      card.__oznQs = true;
      var onHover = function (evt) {
        var el = evt.currentTarget;
        if (theme.config && theme.config.bpSmall) return; // no quick view on mobile
        el.removeEventListener('mouseover', onHover);
        el.removeEventListener('focusin', onHover);
        if (!el.dataset || !el.dataset.productId) return;
        theme.preloadProductModal(
          el.dataset.productHandle,
          el.dataset.productId,
          el.querySelector('.quick-product__btn')
        );
      };
      card.addEventListener('mouseover', onHover);
      card.addEventListener('focusin', onHover);
    });
  }

  // deferReveal=true: batch append during silent autofill; reveal runs once after fillTo.
  function appendCards(grid, cards, instant, deferReveal) {
    var appended = [];
    cards.forEach(function (card) {
      var clone = card.cloneNode(true);
      grid.appendChild(clone);
      appended.push(clone);
    });
    initNewCardsQuickShop(appended);
    if (instant) {
      Reveal.revealAll(grid);
    } else if (!deferReveal) {
      Reveal.setup(grid);
    }
  }

  // ===========================================================================
  // Session state (scroll restore)
  // ===========================================================================

  function stateKey() {
    var params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.delete('section_id');
    params.delete('_oznc');
    params.sort();
    return window.location.pathname + '?' + params.toString();
  }

  function saveState() {
    if (window.__oznNavigatingSearch) return;
    var self = runtime.current;
    if (!self || !self.grid) return;
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          key: stateKey(),
          page: self.loadedThroughPage,
          scrollY: window.scrollY || window.pageYOffset || 0,
          ts: Date.now()
        })
      );
    } catch (e) {
      /* storage unavailable */
    }
  }

  function readState() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || Date.now() - (data.ts || 0) > STATE_MAX_AGE) return null;
      return data;
    } catch (e) {
      return null;
    }
  }

  function clearState() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* noop */
    }
  }

  // ===========================================================================
  // Autofill instance
  // ===========================================================================

  function Autofill(wrapper) {
    this.wrapper = wrapper;
    this.sectionId = wrapper.getAttribute('data-ozn-section-id');
    this.perPage = parseInt(wrapper.getAttribute('data-ozn-per-page'), 10) || 0;
    this.totalPages = parseInt(wrapper.getAttribute('data-ozn-total-pages'), 10) || 1;
    this.currentPage = parseInt(wrapper.getAttribute('data-ozn-current-page'), 10) || 1;
    this.nextPage = this.currentPage + 1;
    this.loadedThroughPage = this.currentPage;
    this.grid = qs(wrapper, SELECTORS.grid);
    this.pagination = qs(wrapper, SELECTORS.pagination);
    this.loadMore = qs(wrapper, SELECTORS.loadMore);
    this.loadMoreBtn = qs(wrapper, SELECTORS.loadMoreBtn);
    this.init();
  }

  Autofill.prototype.hasMore = function () {
    return this.nextPage <= this.totalPages;
  };

  Autofill.prototype.updateControls = function () {
    if (this.pagination) this.pagination.style.display = 'none';
    if (this.loadMore) this.loadMore.hidden = !this.hasMore();
  };

  // Toggle the Impulse loading spinner on the "load more" button.
  Autofill.prototype.setLoading = function (on) {
    if (!this.loadMoreBtn) return;
    this.loadMoreBtn.classList.toggle('btn--loading', !!on);
    this.loadMoreBtn.disabled = !!on;
  };

  // Loads exactly ONE page. silent=true suppresses the button spinner (used by
  // the initial auto-fill before the user interacts).
  Autofill.prototype.loadNext = function (silent) {
    var self = this;
    if (runtime.inFlight || !this.hasMore() || !this.grid) {
      return Promise.resolve();
    }
    runtime.inFlight = true;
    if (!silent) this.setLoading(true);
    var page = this.nextPage;
    return fetchPageCards(this.sectionId, page)
      .then(function (cards) {
        if (cards.length) appendCards(self.grid, cards, false, !!silent);
        self.nextPage = page + 1;
        self.loadedThroughPage = page;
      })
      .catch(function () {
        self.nextPage = self.totalPages + 1; // stop on error
      })
      .then(function () {
        runtime.inFlight = false;
        if (!silent) self.setLoading(false);
        self.updateControls();
      });
  };

  // Initial top-up only: load pages sequentially until we reach `target` cards
  // OR we hit the page cap (prevents the runaway burst that froze the tab).
  Autofill.prototype.fillTo = function (target, maxPages) {
    var self = this;
    if (!this.grid) return Promise.resolve();
    var budget = typeof maxPages === 'number' ? maxPages : MAX_FILL_PAGES;
    var loaded = 0;

    function next() {
      if (
        cardCount(self.grid) >= target ||
        !self.hasMore() ||
        loaded >= budget
      ) {
        self.updateControls();
        return;
      }
      loaded += 1;
      return self.loadNext(true).then(next);
    }

    return Promise.resolve().then(next);
  };

  Autofill.prototype.restoreIfNeeded = function () {
    var saved = readState();
    if (!saved || saved.key !== stateKey() || saved.page <= this.currentPage) {
      return false;
    }
    var self = this;
    var targetPage = Math.min(saved.page, this.totalPages);

    function done() {
      Reveal.revealAll(self.grid);
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          window.scrollTo(0, saved.scrollY);
          clearState();
          self.updateControls();
        });
      });
    }

    function step() {
      if (self.nextPage > targetPage || !self.hasMore()) {
        done();
        return;
      }
      var page = self.nextPage;
      fetchPageCards(self.sectionId, page)
        .then(function (cards) {
          if (cards.length) appendCards(self.grid, cards, true);
          self.nextPage = page + 1;
          self.loadedThroughPage = page;
          step();
        })
        .catch(function () {
          done();
        });
    }

    step();
    return true;
  };

  Autofill.prototype.init = function () {
    var self = this;
    if (!this.grid || !this.perPage) return;

    runtime.current = this;

    if (this.loadMoreBtn) {
      this.loadMoreBtn.addEventListener('click', function () {
        self.loadNext(false);
      });
    }

    if (this.loadMore && 'IntersectionObserver' in window) {
      // One page per intersection. The observer naturally re-fires as the user
      // scrolls (the sentinel leaves and re-enters), so there is no cascade.
      this.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && !self.loadMore.hidden && !runtime.inFlight) {
              self.loadNext(false);
            }
          });
        },
        { rootMargin: '600px 0px' }
      );
      this.observer.observe(this.loadMore);
    }

    this.updateControls();

    var isSearchPage = window.location.pathname.indexOf('/search') !== -1;
    if (isSearchPage) clearState();
    var restored = !isSearchPage && this.restoreIfNeeded();
    if (!restored) {
      this.fillTo(this.perPage, MAX_FILL_PAGES).then(function () {
        if (self.grid) Reveal.setup(self.grid);
      });
    }
  };

  // ===========================================================================
  // Global wiring
  // ===========================================================================

  function bindGlobals() {
    if (runtime.boundGlobals) return;
    runtime.boundGlobals = true;

    window.addEventListener('pagehide', saveState);

    document.addEventListener('click', function (e) {
      if (!runtime.current || !runtime.current.grid) return;
      var link = e.target.closest && e.target.closest('a[href*="/products/"]');
      if (link && runtime.current.grid.contains(link)) {
        saveState();
      }
    });

    window.addEventListener('pageshow', function (evt) {
      if (!evt.persisted) return;
      // bfcache restore (the usual path on mobile "back"): the DOM and JS heap
      // are intact, but history.scrollRestoration='manual' means the browser
      // won't restore scroll for us, so we do it from the saved state. (The full
      // reload path is handled by Autofill.restoreIfNeeded on boot instead.)
      var saved = readState();
      if (saved && saved.key === stateKey()) {
        var y = saved.scrollY;
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            window.scrollTo(0, y);
          });
        });
      }
      clearState();
    });
  }

  function discardStaleScrollState() {
    var saved = readState();
    if (saved && saved.key !== stateKey()) {
      clearState();
    }
  }

  function runBoot() {
    discardStaleScrollState();
    bindResize();

    var wrapper = getWrapper();
    if (wrapper && !wrapper.__oznAutofill) {
      wrapper.__oznAutofill = true;
      bindGlobals();
      new Autofill(wrapper);
      return;
    }

    getGrids().forEach(function (grid) {
      Reveal.setup(grid);
    });
  }

  function boot() {
    runBoot();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('collection:reloaded', function () {
    runtime.inFlight = false;
    runtime.bootPending = false;
    boot();
  });
})();
